import { useState, useMemo, useEffect } from 'react'
import MobileTimePage from './MobileTimePage'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft, ChevronRight, Send, Plus, Check, X, Trash2, ArrowUp,
  FileText, CopyPlus, AlertTriangle, Receipt, RefreshCw, Combine, List, Play,
} from 'lucide-react'
import { CustomerChip } from '@/components/primitives'
import { api } from '@/lib/api/client'
import type { TimeEntry, WeekStatus, WeekStatusValue, ApprovalRow, UtilisationRow, LeaveType, PayrollSyncResult, WorkTreeCustomer, TimeEntryMergeResponse, AvailableHoursEntry } from '@/types/api'
import TimeEntryEditor from '@/components/time/TimeEntryEditor'
import DesktopTimerBar from '@/components/time/DesktopTimerBar'
import { useTimerState, useTimerActions } from '@/lib/timer'
import { MyTimeDashboardContent } from './MyTimePage'
import TeamTimeView from './TeamTimeView'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function chipColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return `oklch(0.52 0.12 ${h % 360})`
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TARGET_HOURS = 40
const MONTHS       = ['January','February','March','April','May','June','July',
                      'August','September','October','November','December']

// ─── Local types ──────────────────────────────────────────────────────────────

interface DayCell {
  id:          number | null
  workedHrs:   number
  billableHrs: number
}

interface WeekRow {
  rowKey:             string
  projectId:          number | null
  projectName:        string | null
  customerId:         number | null
  customerName:       string | null
  phaseId:            number | null
  phaseName:          string | null
  activityId:         number | null
  activityName:       string | null
  /** Legacy column kept for migration data; null for new project entries. */
  articleNumber:      string | null
  articleDescription: string | null
  leaveLabel:         string | null
  invoiceText:        string | null
  notes:              string | null
  days:               DayCell[]
  /** TT sync rollup across the row's days (WI-11887). */
  anySynced:          boolean
  anyFailed:          boolean
}

type SaveResult =
  | { action: 'noop' }
  | { action: 'created'; rowKey: string; dayIndex: number; id: number; workedHrs: number; billableHrs: number }
  | { action: 'updated'; rowKey: string; dayIndex: number; workedHrs: number; billableHrs: number }
  | { action: 'deleted'; rowKey: string; dayIndex: number }

// ─── Date helpers ─────────────────────────────────────────────────────────────

// View range — Day, Work week (Mon-Fri), Week (Mon-Sun), Month (WI-11806).
// All views render the same week-grid layout but with a different `dayCount`.
// Submission and copy-prev-week still operate on a single ISO week and are
// hidden in Month view.

type ViewKind = 'day' | 'work-week' | 'week' | 'month'

const VIEWS: { value: ViewKind; label: string }[] = [
  { value: 'day',       label: 'Day'       },
  { value: 'work-week', label: 'Work week' },
  { value: 'week',      label: 'Week'      },
  { value: 'month',     label: 'Month'     },
]

function startOfDay(d: Date): Date {
  const r = new Date(d); r.setHours(0, 0, 0, 0); return r
}

function mondayOf(d: Date): Date {
  const day = d.getDay()
  const m = new Date(d)
  m.setDate(d.getDate() - (day === 0 ? 6 : day - 1))
  m.setHours(0, 0, 0, 0)
  return m
}

function getWeekStart(offset: number): Date {
  return addDays(mondayOf(new Date()), offset * 7)
}

function toIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(d.getDate() + n)
  return r
}

function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
}

interface ViewRange {
  /** First day in the visible window. */
  start:    Date
  /** Day count (1 / 5 / 7 / 28-31). */
  dayCount: number
  /** Header labels — short names + day-of-month. */
  labels:   string[]
  /** Index of "today" within the range, or -1 when today is outside. */
  todayIndex: number
  /** Human header text, e.g. "May 5 – 11, 2026" or "May 2026" or "Tue 6 May". */
  rangeText: string
  /** Underlying ISO week (Monday). Always defined; for Month it's the week of
   *  `start`. Used for week-status / submit / copy-prev-week which remain
   *  per-week operations. */
  isoWeekStart: Date
}

function getViewRange(view: ViewKind, anchor: Date): ViewRange {
  const today = startOfDay(new Date())
  const days  = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  if (view === 'day') {
    const start = startOfDay(anchor)
    const labels = [`${days[(start.getDay() + 6) % 7]} ${start.getDate()}`]
    return {
      start, dayCount: 1, labels,
      todayIndex: start.getTime() === today.getTime() ? 0 : -1,
      rangeText:  `${days[(start.getDay() + 6) % 7]} ${MONTHS[start.getMonth()].slice(0, 3)} ${start.getDate()}, ${start.getFullYear()}`,
      isoWeekStart: mondayOf(start),
    }
  }

  if (view === 'work-week' || view === 'week') {
    const start = mondayOf(anchor)
    const dayCount = view === 'work-week' ? 5 : 7
    const labels = Array.from({ length: dayCount }, (_, i) => {
      const d = addDays(start, i)
      return `${days[i]} ${d.getDate()}`
    })
    const todayDiff = Math.round((today.getTime() - start.getTime()) / 86400000)
    const end = addDays(start, dayCount - 1)
    const startStr = `${MONTHS[start.getMonth()].slice(0, 3)} ${start.getDate()}`
    const endStr = start.getMonth() === end.getMonth()
      ? `${end.getDate()}`
      : `${MONTHS[end.getMonth()].slice(0, 3)} ${end.getDate()}`
    return {
      start, dayCount, labels,
      todayIndex: todayDiff >= 0 && todayDiff < dayCount ? todayDiff : -1,
      rangeText:  `${startStr} – ${endStr}, ${start.getFullYear()}`,
      isoWeekStart: start,
    }
  }

  // month
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const dayCount = daysInMonth(anchor)
  const labels = Array.from({ length: dayCount }, (_, i) => {
    const d = addDays(start, i)
    return `${days[(d.getDay() + 6) % 7].slice(0, 1)} ${d.getDate()}`
  })
  const todayDiff = Math.round((today.getTime() - start.getTime()) / 86400000)
  return {
    start, dayCount, labels,
    todayIndex: todayDiff >= 0 && todayDiff < dayCount ? todayDiff : -1,
    rangeText:  `${MONTHS[start.getMonth()]} ${start.getFullYear()}`,
    isoWeekStart: mondayOf(start),
  }
}

function buildRows(entries: TimeEntry[], viewStart: Date, dayCount: number): WeekRow[] {
  const map = new Map<string, WeekRow>()
  for (const e of entries) {
    // Activity is the primary discriminator for project entries; legacy
    // entries fall back to article_number; leave entries use the leave code.
    const disc   = e.activityId != null ? `a${e.activityId}` : (e.articleNumber ?? '')
    const rowKey = `${e.projectId ?? 'null'}:${disc}`
    if (!map.has(rowKey)) {
      map.set(rowKey, {
        rowKey,
        projectId:          e.projectId,
        projectName:        e.projectName,
        customerId:         e.customerId,
        customerName:       e.customerName,
        phaseId:            e.phaseId,
        phaseName:          e.phaseName,
        activityId:         e.activityId,
        activityName:       e.activityName,
        articleNumber:      e.articleNumber,
        articleDescription: e.articleDescription,
        leaveLabel:         e.leaveLabel,
        invoiceText:        e.invoiceText,
        notes:              e.notes,
        days: Array.from({ length: dayCount }, () => ({ id: null, workedHrs: 0, billableHrs: 0 })),
        anySynced:          false,
        anyFailed:          false,
      })
    }
    const row      = map.get(rowKey)!
    const dayIndex = Math.round((new Date(e.entryDate).getTime() - viewStart.getTime()) / 86400000)
    if (dayIndex >= 0 && dayIndex < dayCount) {
      row.days[dayIndex] = { id: e.id, workedHrs: e.workedHrs, billableHrs: e.billableHrs }
      if (e.invoiceText) row.invoiceText = e.invoiceText
      if (e.notes)       row.notes       = e.notes
    }
    if (e.ttReportedTimeId != null) row.anySynced = true
    if (e.ttSyncStatus === 'failed') row.anyFailed = true
  }
  return Array.from(map.values())
}

// ─── Mobile detection ────────────────────────────────────────────────────────

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isMobile
}

// ─── TimePage ─────────────────────────────────────────────────────────────────

export default function TimePage() {
  // WI-11806: view = day | work-week | week | month. `anchor` is any date
  // inside the visible window — getViewRange snaps it to the right boundary.
  const [view,    setView]    = useState<ViewKind>('week')
  const [anchor,  setAnchor]  = useState<Date>(() => mondayOf(new Date()))

  const range = useMemo(() => getViewRange(view, anchor), [view, anchor])
  const { start: viewStart, dayCount, labels: weekLabels, todayIndex, rangeText, isoWeekStart } = range
  const viewStartStr     = useMemo(() => toIso(viewStart), [viewStart])
  const viewEndStr       = useMemo(() => toIso(addDays(viewStart, dayCount - 1)), [viewStart, dayCount])
  const isoWeekStartStr  = useMemo(() => toIso(isoWeekStart), [isoWeekStart])
  // Submit/copy-prev-week only make sense for a single-week view (Mon-Sun and
  // Mon-Fri share the same ISO week; Day view is always inside one week).
  // Month view spans many weeks → those operations are hidden.
  const isWeekScopedView = view !== 'month'

  // WI-11829: dashboard is the default landing tab; "mine" remains the
  // weekly-grid time-entry view (renamed to "Log time" in the tab strip).
  const [tab,         setTab]         = useState<'dashboard' | 'mine' | 'team' | 'approvals' | 'utilisation'>('dashboard')
  const [addRowOpen,  setAddRowOpen]  = useState(false)
  const [editRow,     setEditRow]     = useState<WeekRow | null>(null)
  const [copyLoading, setCopyLoading] = useState(false)
  // WI-11825: per-entry list panel (edit single entries + merge).
  const [entriesPanelOpen, setEntriesPanelOpen] = useState(false)

  const queryClient = useQueryClient()

  const { data: entriesData } = useQuery({
    queryKey: ['time', 'entries', viewStartStr, viewEndStr],
    queryFn:  () => api.get<TimeEntry[]>(`/time/entries?from=${viewStartStr}&to=${viewEndStr}`),
  })

  const { data: weekStatusData } = useQuery({
    queryKey: ['time', 'week', isoWeekStartStr],
    queryFn:  () => api.get<WeekStatus>(`/time/weeks/${isoWeekStartStr}`),
  })

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn:  () => api.get<{ roles: string[]; canImpersonate: boolean; canViewOthersTime: boolean }>('/me'),
  })
  const isAdmin           = me?.roles.includes('Admin')              ?? false
  const isPayroll         = me?.roles.includes('Payroll')            ?? false
  const isTimeAdmin       = isAdmin || (me?.roles.includes('Time Administrator') ?? false)
  const canViewOthers     = me?.canViewOthersTime ?? false

  const { data: availData } = useQuery({
    queryKey: ['time', 'my-available-hours', viewStartStr, viewEndStr],
    queryFn:  () => api.get<AvailableHoursEntry[]>(`/time/my-available-hours?from=${viewStartStr}&to=${viewEndStr}`),
  })
  const availByDate = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {}
    for (const e of availData ?? []) map[e.date] = e.availableHours
    return map
  }, [availData])

  const status     = (weekStatusData?.status ?? 'draft') as WeekStatusValue
  const isInvoiced = !!weekStatusData?.invoicedAt
  const isReadOnly = status === 'submitted' || status === 'approved' || isInvoiced

  const [localRows, setLocalRows] = useState<WeekRow[]>([])

  useEffect(() => {
    setLocalRows(entriesData ? buildRows(entriesData, viewStart, dayCount) : [])
  }, [entriesData, viewStartStr, dayCount]) // eslint-disable-line react-hooks/exhaustive-deps

  const totalWorked     = localRows.reduce((s, r) => s + r.days.reduce((a, d) => a + d.workedHrs,   0), 0)
  const totalBillable   = localRows.reduce((s, r) => s + r.days.reduce((a, d) => a + d.billableHrs, 0), 0)
  const utilization     = totalWorked > 0 ? (totalBillable / totalWorked) * 100 : 0
  const uniqueCustomers = new Set(localRows.filter(r => r.customerId !== null).map(r => r.customerId)).size

  // Total available hours for the current view period (sum of per-day available hours).
  // Falls back to the hardcoded TARGET_HOURS constant when data hasn't loaded yet.
  const totalAvailable = useMemo(() => {
    if (!availData?.length) return TARGET_HOURS
    let sum = 0
    for (let i = 0; i < dayCount; i++) {
      const d = addDays(viewStart, i)
      sum += availByDate[toIso(d)] ?? 0
    }
    return sum || TARGET_HOURS
  }, [availData, availByDate, viewStart, dayCount])

  // ─── Copy from previous week ─────────────────────────────────────────────
  // Operates on the ISO week containing the current view (Day / Work week /
  // Week). Hidden in Month view.

  const copyPrevWeek = async () => {
    setCopyLoading(true)
    try {
      const prevWeekStart = toIso(addDays(isoWeekStart, -7))
      const prevEntries   = await api.get<TimeEntry[]>(`/time/entries?weekStart=${prevWeekStart}`)
      const prevRows      = buildRows(prevEntries, addDays(isoWeekStart, -7), 7)
      prevRows.forEach(row => addRow({
        rowKey: row.rowKey, projectId: row.projectId, projectName: row.projectName,
        customerId: row.customerId, customerName: row.customerName,
        phaseId: row.phaseId, phaseName: row.phaseName,
        activityId: row.activityId, activityName: row.activityName,
        articleNumber: row.articleNumber, articleDescription: row.articleDescription,
        leaveLabel: row.leaveLabel,
        invoiceText: null, notes: null,
        anySynced: false, anyFailed: false,
      }))
    } finally {
      setCopyLoading(false)
    }
  }

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async (payload: { rowKey: string; dayIndex: number; workedHrs: number }): Promise<SaveResult> => {
      const row = localRows.find(r => r.rowKey === payload.rowKey)
      if (!row) return { action: 'noop' }

      const day         = row.days[payload.dayIndex]
      // Project entries default to fully billable; leave entries are 0.
      const billableHrs = row.projectId != null ? payload.workedHrs : 0
      const entryDate   = toIso(addDays(viewStart, payload.dayIndex))

      if (payload.workedHrs <= 0 && day.id !== null) {
        await api.delete<unknown>(`/time/entries/${day.id}`)
        return { action: 'deleted', rowKey: payload.rowKey, dayIndex: payload.dayIndex }
      }
      if (payload.workedHrs <= 0) return { action: 'noop' }

      if (day.id !== null) {
        await api.put<unknown>(`/time/entries/${day.id}`, { workedHrs: payload.workedHrs, billableHrs })
        return { action: 'updated', rowKey: payload.rowKey, dayIndex: payload.dayIndex, workedHrs: payload.workedHrs, billableHrs }
      }

      const res = await api.post<{ id: number }>('/time/entries', {
        projectId: row.projectId, phaseId: row.phaseId, activityId: row.activityId,
        // Leave entries still need the leave-type code in articleNumber.
        articleNumber: row.projectId == null ? row.articleNumber : null,
        entryDate,
        workedHrs: payload.workedHrs, billableHrs,
      })
      return { action: 'created', rowKey: payload.rowKey, dayIndex: payload.dayIndex, id: res.id, workedHrs: payload.workedHrs, billableHrs }
    },
    onSuccess: (result) => {
      if (result.action === 'noop') return
      setLocalRows(prev => prev.map(r => {
        if (r.rowKey !== result.rowKey) return r
        const days = r.days.map((d, i) => {
          if (i !== result.dayIndex) return d
          if (result.action === 'deleted') return { id: null, workedHrs: 0, billableHrs: 0 }
          if (result.action === 'created') return { id: result.id, workedHrs: result.workedHrs, billableHrs: result.billableHrs }
          if (result.action === 'updated') return { ...d, workedHrs: result.workedHrs, billableHrs: result.billableHrs }
          return d
        })
        return { ...r, days }
      }))
    },
  })

  const deleteRowMutation = useMutation({
    mutationFn: async (rowKey: string) => {
      const row = localRows.find(r => r.rowKey === rowKey)
      if (!row) return
      for (const day of row.days) {
        if (day.id !== null) await api.delete<unknown>(`/time/entries/${day.id}`)
      }
    },
    onSuccess: (_, rowKey) => {
      setLocalRows(prev => prev.filter(r => r.rowKey !== rowKey))
    },
  })

  const submitMutation = useMutation({
    mutationFn: () => api.post<unknown>(`/time/weeks/${isoWeekStartStr}/submit`, {}),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['time', 'week', isoWeekStartStr] }),
  })

  const addRow = (newRow: Omit<WeekRow, 'days'>) => {
    if (localRows.some(r => r.rowKey === newRow.rowKey)) return
    setLocalRows(prev => [...prev, {
      ...newRow,
      days: Array.from({ length: dayCount }, () => ({ id: null, workedHrs: 0, billableHrs: 0 })),
    }])
  }

  const onRowTextSaved = (rowKey: string, invoiceText: string | null, notes: string | null) => {
    setLocalRows(prev => prev.map(r => r.rowKey === rowKey ? { ...r, invoiceText, notes } : r))
    setEditRow(null)
    queryClient.invalidateQueries({ queryKey: ['time', 'entries', viewStartStr, viewEndStr] })
  }

  // Navigation step depends on view: Day = ±1 day, Work-week / Week = ±1 week,
  // Month = ±1 month.
  const stepBack = () => setAnchor(a => {
    if (view === 'day')   return addDays(a, -1)
    if (view === 'month') { const r = new Date(a); r.setMonth(r.getMonth() - 1); r.setDate(1); return r }
    return addDays(a, -7)
  })
  const stepForward = () => setAnchor(a => {
    if (view === 'day')   return addDays(a, 1)
    if (view === 'month') { const r = new Date(a); r.setMonth(r.getMonth() + 1); r.setDate(1); return r }
    return addDays(a, 7)
  })
  const jumpToToday = () => setAnchor(view === 'month' ? new Date(new Date().getFullYear(), new Date().getMonth(), 1) : mondayOf(new Date()))
  const isMobile = useIsMobile()

  if (isMobile) {
    // Mobile keeps its own day-based UX. Pass the current week offset
    // derived from the desktop's view anchor so the two stay in sync.
    const weekOffset = Math.round((isoWeekStart.getTime() - mondayOf(new Date()).getTime()) / (7 * 86400000))
    return (
      <MobileTimePage
        weekOffset={weekOffset}
        onPrevWeek={() => setAnchor(a => addDays(a, -7))}
        onNextWeek={() => setAnchor(a => addDays(a, 7))}
        onThisWeek={jumpToToday}
      />
    )
  }

  return (
    <div className="page">

      {/* Header */}
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Time Reporting</div>
          <h1 className="page-title">{rangeText}</h1>
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            {isWeekScopedView && status === 'draft'     && <span className="badge badge-info">Draft · not submitted</span>}
            {isWeekScopedView && status === 'submitted' && <span className="badge badge-warn">Submitted · awaiting approval</span>}
            {isWeekScopedView && status === 'approved'  && !isInvoiced && <span className="badge badge-ok">Approved</span>}
            {isWeekScopedView && status === 'rejected'  && <span className="badge badge-err">Rejected</span>}
            {isWeekScopedView && isInvoiced             && <span className="badge badge-ok"><Receipt size={10} style={{ display: 'inline', marginRight: 3 }} />Invoiced{weekStatusData?.fortnoxInvRef ? ` · ${weekStatusData.fortnoxInvRef}` : ''}</span>}
            {!isWeekScopedView && <span className="badge">Month overview · submit per week</span>}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <div style={{ display: 'flex', gap: 4, marginRight: 8 }}>
            {VIEWS.map(v => (
              <button
                key={v.value}
                className={`chip${view === v.value ? ' active' : ''}`}
                onClick={() => setView(v.value)}
              >
                {v.label}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={stepBack} aria-label="Previous">
            <ChevronLeft size={14} />
          </button>
          <button className="btn" onClick={jumpToToday}>
            {view === 'day' ? 'Today' : view === 'month' ? 'This month' : 'This week'}
          </button>
          <button className="btn btn-ghost btn-icon" onClick={stepForward} aria-label="Next">
            <ChevronRight size={14} />
          </button>
          <button
            className="btn btn-ghost"
            style={{ gap: 6 }}
            onClick={() => setEntriesPanelOpen(true)}
            title="Edit individual entries or merge timer entries"
          >
            <List size={14} />
            Manage entries
          </button>
          {isWeekScopedView && (status === 'draft' || status === 'rejected') && (
            <button
              className="btn btn-brand"
              style={{ gap: 6 }}
              onClick={() => submitMutation.mutate()}
              disabled={submitMutation.isPending || totalWorked === 0}
            >
              <Send size={14} />
              {submitMutation.isPending ? 'Submitting…' : status === 'rejected' ? 'Resubmit week' : 'Submit week'}
            </button>
          )}
        </div>
      </div>

      {/* Timer bar (WI-11828) — Start / running card / Stop, shared state with mobile */}
      <DesktopTimerBar />

      {/* Rejection banner */}
      {status === 'rejected' && weekStatusData?.rejectionNote && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 16px', borderRadius: 8,
          background: 'var(--ns-err-soft)', border: '1px solid var(--ns-err)',
        }}>
          <AlertTriangle size={15} style={{ color: 'var(--ns-err)', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ns-err)', marginBottom: 2 }}>Week rejected</div>
            <div style={{ fontSize: 13 }}>{weekStatusData.rejectionNote}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Fix the issues above, then click Resubmit week.</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab${tab === 'dashboard' ? ' active' : ''}`} onClick={() => setTab('dashboard')}>My time</button>
        <button className={`tab${tab === 'mine'      ? ' active' : ''}`} onClick={() => setTab('mine')}>Log time</button>
        {canViewOthers && (
          <button className={`tab${tab === 'team' ? ' active' : ''}`} onClick={() => setTab('team')}>Team time</button>
        )}
        {isAdmin && (
          <button className={`tab${tab === 'approvals' ? ' active' : ''}`} onClick={() => setTab('approvals')}>Approvals</button>
        )}
        {isAdmin && (
          <button className={`tab${tab === 'utilisation' ? ' active' : ''}`} onClick={() => setTab('utilisation')}>Team utilisation</button>
        )}
      </div>

      {tab === 'dashboard' && (
        <MyTimeDashboardContent showHeader={false} onLogTime={() => setTab('mine')} />
      )}

      {tab === 'mine' && (
        <>
        <div className="stat-grid" style={{ marginTop: 16 }}>
          <div className="stat">
            <div className="stat-label">
              Logged this {view === 'day' ? 'day' : view === 'month' ? 'month' : 'week'}
            </div>
            <div className="stat-value tabular">
              {totalWorked.toFixed(1)}
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ns-text-muted)', marginLeft: 4 }}>h</span>
            </div>
            {view !== 'month' && (
              <>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                  Target: {totalAvailable}h
                </div>
                <div className="progress" style={{ marginTop: 6 }}>
                  <div className="progress-bar" style={{
                    width: `${Math.min(100, totalAvailable > 0 ? (totalWorked / totalAvailable) * 100 : 0)}%`,
                  }} />
                </div>
              </>
            )}
          </div>
          <div className="stat">
            <div className="stat-label">Billable</div>
            <div className="stat-value tabular">
              {totalBillable.toFixed(1)}
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ns-text-muted)', marginLeft: 4 }}>h</span>
            </div>
            <div className="stat-delta up">
              <ArrowUp size={12} />{utilization.toFixed(0)}% utilization
            </div>
          </div>
          <div className="stat">
            <div className="stat-label">Customers</div>
            <div className="stat-value tabular">{uniqueCustomers}</div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
              Active this {view === 'day' ? 'day' : view === 'month' ? 'month' : 'week'}
            </div>
          </div>
          <div className="stat">
            <div className="stat-label">Status</div>
            <div style={{ marginTop: 6 }}>
              {!isWeekScopedView && <span className="badge">Per week</span>}
              {isWeekScopedView && isInvoiced             && <span className="badge badge-ok">Invoiced</span>}
              {isWeekScopedView && !isInvoiced && status === 'draft'     && <span className="badge badge-info">Draft</span>}
              {isWeekScopedView && !isInvoiced && status === 'submitted' && <span className="badge badge-warn">Submitted</span>}
              {isWeekScopedView && !isInvoiced && status === 'approved'  && <span className="badge badge-ok">Approved</span>}
              {isWeekScopedView && !isInvoiced && status === 'rejected'  && <span className="badge badge-err">Rejected</span>}
            </div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
              {!isWeekScopedView && 'Switch to a week view to submit'}
              {isWeekScopedView && status === 'draft'    && 'Submit by Mon 09:00'}
              {isWeekScopedView && status === 'rejected' && 'Please revise and resubmit'}
              {isWeekScopedView && status === 'approved' && weekStatusData?.approvedByName && `Approved by ${weekStatusData.approvedByName}`}
              {isWeekScopedView && isInvoiced            && weekStatusData?.fortnoxInvRef && `Ref: ${weekStatusData.fortnoxInvRef}`}
            </div>
          </div>
        </div>
        <MyWeekView
          rows={localRows}
          weekLabels={weekLabels}
          todayIndex={todayIndex}
          viewStart={viewStart}
          availByDate={availByDate}
          isReadOnly={isReadOnly}
          totalWorked={totalWorked}
          copyLoading={copyLoading}
          /** Copy from previous week is week-scoped → hide in Month view. */
          showCopyPrevWeek={isWeekScopedView}
          onSave={(rowKey, dayIndex, workedHrs) => saveMutation.mutate({ rowKey, dayIndex, workedHrs })}
          onDeleteRow={(rowKey) => {
            if (confirm('Remove this row and all its entries for this view?')) deleteRowMutation.mutate(rowKey)
          }}
          onAddRow={() => setAddRowOpen(true)}
          onEditRow={(row) => setEditRow(row)}
          onCopyPrevWeek={copyPrevWeek}
        />
        </>
      )}
      {tab === 'team'        && canViewOthers && <TeamTimeView isTimeAdmin={isTimeAdmin} />}
      {tab === 'approvals'   && isAdmin && <ApprovalsView canSyncPayroll={isPayroll} />}
      {tab === 'utilisation' && isAdmin && <UtilisationView weekStartStr={isoWeekStartStr} />}

      {addRowOpen && (
        <AddRowDrawer
          onClose={() => setAddRowOpen(false)}
          onAdd={(row) => { addRow(row); setAddRowOpen(false) }}
          existingRowKeys={new Set(localRows.map(r => r.rowKey))}
        />
      )}

      {editRow && (
        <RowEditDrawer
          row={editRow}
          weekStart={viewStart}
          onClose={() => setEditRow(null)}
          onSaved={onRowTextSaved}
        />
      )}

      {entriesPanelOpen && (
        <EntriesPanel
          entries={entriesData ?? []}
          weekStatus={status}
          isReadOnly={isReadOnly}
          onClose={() => setEntriesPanelOpen(false)}
        />
      )}

    </div>
  )
}

// ─── My week view ─────────────────────────────────────────────────────────────

interface MyWeekViewProps {
  rows:             WeekRow[]
  weekLabels:       string[]
  todayIndex:       number
  viewStart:        Date
  availByDate:      Record<string, number>
  isReadOnly:       boolean
  totalWorked:      number
  copyLoading:      boolean
  /** Hide the "Copy previous week" button in Month view (multi-week scope). */
  showCopyPrevWeek: boolean
  onSave:           (rowKey: string, dayIndex: number, workedHrs: number) => void
  onDeleteRow:      (rowKey: string) => void
  onAddRow:         () => void
  onEditRow:        (row: WeekRow) => void
  onCopyPrevWeek:   () => void
}

function MyWeekView({ rows, weekLabels, todayIndex, viewStart, availByDate, isReadOnly, totalWorked, copyLoading, showCopyPrevWeek, onSave, onDeleteRow, onAddRow, onEditRow, onCopyPrevWeek }: MyWeekViewProps) {
  // Variable column count (1 / 5 / 7 / 28-31). For Month view the day cells
  // are narrower so the grid fits without needing horizontal scroll on 1280+.
  const dayColWidth = weekLabels.length > 7 ? 36 : 56
  return (
    <div className="card" style={{ overflowX: 'auto' }}>
      <table className="table" style={{ minWidth: weekLabels.length > 7 ? 1100 : undefined }}>
        <thead>
          <tr>
            <th style={{ minWidth: 180 }}>Project</th>
            <th style={{ minWidth: 160 }}>Activity</th>
            {weekLabels.map((d, i) => {
              const dateStr = toIso(addDays(viewStart, i))
              const avail   = availByDate[dateStr]
              const isHoliday = avail === 0
              const isPartDay = avail != null && avail > 0 && avail < 8
              return (
                <th key={i} style={{
                  width: dayColWidth, textAlign: 'center',
                  color:      i >= 5 && weekLabels.length === 7 ? 'var(--ns-text-subtle)' : undefined,
                  background: i === todayIndex ? 'var(--ns-brand-soft)' : undefined,
                  fontSize:   weekLabels.length > 7 ? 11 : undefined,
                  position:   'relative',
                }}>
                  {d}
                  {isHoliday && (
                    <div title="Public holiday — 0h available" style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: 'oklch(0.72 0.052 198)',
                      margin: '2px auto 0',
                    }} />
                  )}
                  {isPartDay && (
                    <div style={{ fontSize: 9, color: 'var(--ns-text-muted)', marginTop: 1 }}>
                      {avail}h
                    </div>
                  )}
                </th>
              )
            })}
            <th style={{ width: 64, textAlign: 'right' }}>Total</th>
            <th style={{ width: 52 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rowTotal   = row.days.reduce((s, d) => s + d.workedHrs, 0)
            const isBillable = row.projectId != null
            const hasText    = !!(row.invoiceText || row.notes)

            return (
              <tr key={row.rowKey}>
                <td>
                  {row.leaveLabel ? (
                    <div className="row" style={{ gap: 7 }}>
                      <span style={{
                        width: 18, height: 18, borderRadius: 4,
                        background: 'oklch(0.88 0.06 300)', color: 'oklch(0.35 0.12 300)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9.5, fontWeight: 700, fontFamily: 'JetBrains Mono', flexShrink: 0,
                      }}>AB</span>
                      <span style={{ fontWeight: 500 }}>Absence / Leave</span>
                    </div>
                  ) : row.projectId ? (
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 12.5 }}>{row.projectName}</div>
                      {row.customerName && (
                        <div style={{ marginTop: 3 }}>
                          <CustomerChip
                            name={row.customerName}
                            short={row.customerName.slice(0, 2).toUpperCase()}
                            color={chipColor(row.customerName)}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="row" style={{ gap: 7 }}>
                      <span style={{
                        width: 18, height: 18, borderRadius: 4,
                        background: 'var(--ns-surface-3)', color: 'var(--ns-text-muted)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9.5, fontWeight: 700, fontFamily: 'JetBrains Mono', flexShrink: 0,
                      }}>NS</span>
                      <span style={{ fontWeight: 500 }}>Numberskills internal</span>
                    </div>
                  )}
                </td>

                <td>
                  <div style={{ fontSize: 12.5 }}>
                    {row.leaveLabel
                      ? row.leaveLabel
                      : (row.activityName ?? <span className="muted">—</span>)}
                  </div>
                  <div style={{ marginTop: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {row.leaveLabel ? (
                      <span className="badge" style={{ fontSize: 10, background: 'oklch(0.88 0.06 300)', color: 'oklch(0.35 0.12 300)', borderColor: 'oklch(0.80 0.08 300)' }}>Absence</span>
                    ) : isBillable ? (
                      <span className="badge badge-brand" style={{ fontSize: 10 }}>Billable</span>
                    ) : (
                      <span className="badge" style={{ fontSize: 10 }}>Internal</span>
                    )}
                    {row.phaseName && row.phaseName !== 'Project Execution' && (
                      <span className="badge" style={{ fontSize: 10 }}>{row.phaseName}</span>
                    )}
                    {row.anyFailed ? (
                      <span className="badge badge-err" style={{ fontSize: 10 }} title="TimeTracker sync failed; queued for retry">Sync failed</span>
                    ) : row.anySynced && (
                      <span className="badge badge-ok" style={{ fontSize: 10 }} title="Synced to TimeTracker">Certified</span>
                    )}
                  </div>
                </td>

                {row.days.map((day, di) => (
                  <TimeCell
                    key={di}
                    day={day}
                    isToday={di === todayIndex}
                    isReadOnly={isReadOnly}
                    isWeekend={di >= 5}
                    onSave={(hrs) => onSave(row.rowKey, di, hrs)}
                  />
                ))}

                <td className="mono tabular" style={{ textAlign: 'right', fontWeight: 600 }}>
                  {rowTotal > 0 ? rowTotal.toFixed(1) : ''}
                </td>

                <td>
                  <div style={{ display: 'flex', gap: 2 }}>
                    <button
                      className="btn btn-ghost btn-icon"
                      title={hasText ? 'Edit invoice text / notes' : 'Add invoice text / notes'}
                      style={{ color: hasText ? 'var(--ns-brand)' : 'var(--ns-text-subtle)' }}
                      onClick={() => onEditRow(row)}
                    >
                      <FileText size={12} />
                    </button>
                    {!isReadOnly && (
                      <button
                        className="btn btn-ghost btn-icon"
                        title="Remove row"
                        style={{ color: 'var(--ns-text-subtle)' }}
                        onClick={() => onDeleteRow(row.rowKey)}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}

          {/* Totals row */}
          <tr style={{ background: 'var(--ns-surface-2)' }}>
            <td colSpan={2}>
              {!isReadOnly && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" style={{ gap: 5 }} onClick={onAddRow}>
                    <Plus size={12} />Add row
                  </button>
                  {showCopyPrevWeek && (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ gap: 5 }}
                      onClick={onCopyPrevWeek}
                      disabled={copyLoading}
                    >
                      <CopyPlus size={12} />{copyLoading ? 'Copying…' : 'Copy prev week'}
                    </button>
                  )}
                </div>
              )}
            </td>
            {weekLabels.map((_, i) => {
              const dayTotal  = rows.reduce((s, r) => s + (r.days[i]?.workedHrs ?? 0), 0)
              const dateStr   = toIso(addDays(viewStart, i))
              const avail     = availByDate[dateStr]
              const isOver    = avail != null && dayTotal > avail && avail > 0
              return (
                <td key={i} className="mono tabular" style={{
                  textAlign: 'center', fontWeight: 600,
                  background: i === todayIndex ? 'var(--ns-brand-soft)' : undefined,
                  color: isOver ? 'oklch(0.55 0.18 30)' : undefined,
                }}
                  title={isOver ? `${dayTotal.toFixed(1)}h logged, ${avail}h available` : undefined}
                >
                  {dayTotal > 0 ? dayTotal.toFixed(1) : ''}
                </td>
              )
            })}
            <td className="mono tabular" style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
              {totalWorked.toFixed(1)}
            </td>
            <td />
          </tr>
        </tbody>
      </table>

      {rows.length === 0 && (
        <div className="muted" style={{ textAlign: 'center', padding: 32, fontSize: 13 }}>
          No rows yet — click <strong>Add row</strong> to start, or <strong>Copy prev week</strong> to bring in last week's projects.
        </div>
      )}
    </div>
  )
}

// ─── Time cell ────────────────────────────────────────────────────────────────

interface TimeCellProps {
  day:        DayCell
  isToday:    boolean
  isWeekend:  boolean
  isReadOnly: boolean
  onSave:     (workedHrs: number) => void
}

function TimeCell({ day, isToday, isWeekend, isReadOnly, onSave }: TimeCellProps) {
  const [val, setVal] = useState(day.workedHrs > 0 ? String(day.workedHrs) : '')

  useEffect(() => {
    setVal(day.workedHrs > 0 ? String(day.workedHrs) : '')
  }, [day.workedHrs, day.id])

  const numVal  = parseFloat(val) || 0
  const hasValue = numVal > 0

  return (
    <td style={{
      padding: 4,
      background: isToday ? 'var(--ns-brand-soft)' : undefined,
      opacity:    isWeekend ? 0.6 : undefined,
    }}>
      <input
        className="mono tabular"
        type="text"
        value={val}
        placeholder="—"
        readOnly={isReadOnly}
        onChange={e => !isReadOnly && setVal(e.target.value)}
        onFocus={e => { if (!isReadOnly) e.currentTarget.style.borderColor = 'var(--ns-brand-strong)' }}
        onBlur={e => {
          e.currentTarget.style.borderColor = 'transparent'
          const num = parseFloat(e.currentTarget.value) || 0
          if (num !== day.workedHrs) onSave(num)
        }}
        style={{
          width: '100%', height: 30, textAlign: 'center',
          border: '1px solid transparent',
          background: hasValue ? 'oklch(from var(--ns-brand-strong) l c h / 0.15)' : 'transparent',
          borderRadius: 4, padding: '0 4px',
          color: 'var(--ns-text)', fontSize: 12.5, outline: 'none',
          fontFamily: 'inherit',
          cursor: isReadOnly ? 'default' : 'text',
        }}
      />
    </td>
  )
}

// ─── Row edit drawer (invoice text + notes) ───────────────────────────────────

interface RowEditDrawerProps {
  row:       WeekRow
  weekStart: Date
  onClose:   () => void
  onSaved:   (rowKey: string, invoiceText: string | null, notes: string | null) => void
}

function RowEditDrawer({ row, weekStart, onClose, onSaved }: RowEditDrawerProps) {
  const [invoiceText, setInvoiceText] = useState(row.invoiceText ?? '')
  const [notes,       setNotes]       = useState(row.notes ?? '')

  const activeDays = row.days
    .map((day, i) => ({ ...day, date: toIso(addDays(weekStart, i)) }))
    .filter(day => day.id !== null)

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const day of activeDays) {
        await api.patch(`/time/entries/${day.id}`, {
          invoiceText: invoiceText.trim() || null,
          notes:       notes.trim()       || null,
        })
      }
    },
    onSuccess: () => onSaved(row.rowKey, invoiceText.trim() || null, notes.trim() || null),
  })

  const projectLabel = row.projectId
    ? `${row.projectName ?? ''}${row.customerName ? ` · ${row.customerName}` : ''}`
    : 'Numberskills internal'

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 400,
        background: 'var(--ns-surface)', borderLeft: '1px solid var(--ns-border)',
        zIndex: 50, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ns-border)' }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>Invoice text & notes</h2>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {projectLabel}{row.activityName ? ` · ${row.activityName}` : (row.articleDescription ? ` · ${row.articleDescription}` : '')}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {activeDays.length === 0 && (
            <p className="muted" style={{ fontSize: 13 }}>No hours logged this week for this row. Add hours first.</p>
          )}

          <div>
            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 5, color: 'var(--ns-text-muted)' }}>
              Invoice text
            </label>
            <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Appears on the invoice line. Applied to all {activeDays.length} day{activeDays.length !== 1 ? 's' : ''} with logged hours.
            </p>
            <textarea
              className="input"
              style={{ width: '100%', resize: 'vertical', minHeight: 72 }}
              value={invoiceText}
              onChange={e => setInvoiceText(e.target.value)}
              placeholder="e.g. Analysis and design work — sprint 3"
              rows={3}
              autoFocus
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 5, color: 'var(--ns-text-muted)' }}>
              Internal notes
            </label>
            <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>Not shown on the invoice.</p>
            <textarea
              className="input"
              style={{ width: '100%', resize: 'vertical' }}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes for this week's entries…"
              rows={2}
            />
          </div>

        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--ns-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-brand"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || activeDays.length === 0}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Add row drawer ───────────────────────────────────────────────────────────

interface AddRowDrawerProps {
  onClose:         () => void
  onAdd:           (row: Omit<WeekRow, 'days'>) => void
  existingRowKeys: Set<string>
}

function AddRowDrawer({ onClose, onAdd, existingRowKeys }: AddRowDrawerProps) {
  const [mode, setMode] = useState<'work' | 'leave'>('work')

  // WI-11807: full Customer → Project → Phase → Activity cascade with a
  // Mine / All toggle. Tree comes from /time/work-tree, the same endpoint
  // the mobile picker uses, so the two surfaces stay in sync.
  const [scope, setScope] = useState<'mine' | 'all'>('mine')

  const { data: workTree = [] } = useQuery({
    queryKey: ['time', 'work-tree', scope],
    queryFn:  () => api.get<WorkTreeCustomer[]>(`/time/work-tree?scope=${scope}`),
  })

  const { data: leaveTypes = [] } = useQuery({
    queryKey: ['time', 'leave-types'],
    queryFn:  () => api.get<LeaveType[]>('/time/leave-types'),
  })

  const [customerId, setCustomerId] = useState<string | number | null>(null)
  const [projectId,  setProjectId]  = useState<number | null>(null)
  const [phaseId,    setPhaseId]    = useState<number | null>(null)
  const [activityId, setActivityId] = useState<number | null>(null)
  const [leaveCode,  setLeaveCode]  = useState('')

  // Reset selection when scope flips — the tree may not contain the prior
  // customer/project anymore.
  useEffect(() => {
    setCustomerId(null); setProjectId(null); setPhaseId(null); setActivityId(null)
  }, [scope])

  const customer = workTree.find(c => c.id === customerId) ?? null
  const project  = customer?.projects.find(p => p.id === projectId) ?? null
  const phase    = project?.phases.find(p => p.id === phaseId) ?? null
  const activity = phase?.activities.find(a => a.id === activityId) ?? null

  // Auto-cascade: when only one option exists at the next level, pick it.
  useEffect(() => {
    if (!customer) return
    if (customer.projects.length === 1) {
      const p = customer.projects[0]
      setProjectId(p.id)
      if (p.phases.length === 1) {
        const ph = p.phases[0]
        setPhaseId(ph.id)
        if (ph.activities.length === 1) setActivityId(ph.activities[0].id)
      }
    }
  }, [customerId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (project && project.phases.length === 1) setPhaseId(project.phases[0].id)
  }, [projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase && phase.activities.length === 1) setActivityId(phase.activities[0].id)
  }, [phaseId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Work mode — discriminator is the activity so the row can co-exist with
  // other activities on the same project.
  const workDisc        = activityId != null ? `a${activityId}` : ''
  const workRowKey      = `${projectId ?? 'null'}:${workDisc}`
  const workIsDuplicate = workDisc ? existingRowKeys.has(workRowKey) : false
  const canAddWork      = !!project && !!phase && !!activity && !workIsDuplicate

  // Leave mode
  const leaveRowKey      = `null:${leaveCode}`
  const leaveIsDuplicate = leaveCode ? existingRowKeys.has(leaveRowKey) : false
  const canAddLeave      = !!leaveCode && !leaveIsDuplicate

  const handleAdd = () => {
    if (mode === 'leave') {
      if (!canAddLeave) return
      const lt = leaveTypes.find(l => l.code === leaveCode)
      onAdd({
        rowKey:             leaveRowKey,
        projectId:          null,
        projectName:        null,
        customerId:         null,
        customerName:       null,
        phaseId:            null,
        phaseName:          null,
        activityId:         null,
        activityName:       null,
        articleNumber:      leaveCode,
        articleDescription: lt?.label ?? leaveCode,
        leaveLabel:         lt?.label ?? leaveCode,
        invoiceText:        null,
        notes:              null,
        anySynced:          false,
        anyFailed:          false,
      })
    } else {
      if (!canAddWork || !customer || !project || !phase || !activity) return
      // Customer id from the tree may be 'internal' (string) for projects
      // with no customer; surface that as null on the row.
      const cid = typeof customer.id === 'number' ? customer.id : null
      onAdd({
        rowKey:             workRowKey,
        projectId:          project.id,
        projectName:        project.name,
        customerId:         cid,
        customerName:       cid != null ? customer.name : null,
        phaseId:             phase.id,
        phaseName:           phase.name,
        activityId:          activity.id,
        activityName:        activity.name,
        articleNumber:      null,
        articleDescription: null,
        leaveLabel:         null,
        invoiceText:        null,
        notes:              null,
        anySynced:          false,
        anyFailed:          false,
      })
    }
  }

  const canAdd = mode === 'leave' ? canAddLeave : canAddWork

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 380,
        background: 'var(--ns-surface)', borderLeft: '1px solid var(--ns-border)',
        zIndex: 50, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ns-border)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600 }}>Add row</h2>
        </div>

        {/* Mode tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--ns-border)' }}>
          {(['work', 'leave'] as const).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                flex: 1, padding: '10px 0', fontSize: 12.5, fontWeight: 500,
                border: 'none', borderBottom: m === mode ? '2px solid var(--ns-brand-strong)' : '2px solid transparent',
                background: 'none', color: m === mode ? 'var(--ns-brand-strong)' : 'var(--ns-text-muted)',
                cursor: 'pointer',
              }}
            >
              {m === 'work' ? 'Project work' : 'Leave & Absence'}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {mode === 'work' && (
            <>
              {/* Mine / All scope toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  display: 'inline-flex', background: 'var(--ns-surface-2)',
                  borderRadius: 8, padding: 3,
                }}>
                  {(['mine', 'all'] as const).map(s => (
                    <button
                      key={s}
                      onClick={() => setScope(s)}
                      style={{
                        padding: '5px 14px', fontSize: 12.5, fontWeight: scope === s ? 600 : 500,
                        color: scope === s ? 'var(--ns-text)' : 'var(--ns-text-muted)',
                        background: scope === s ? 'var(--ns-surface)' : 'transparent',
                        border: 'none', borderRadius: 6, cursor: 'pointer',
                        boxShadow: scope === s ? '0 1px 2px oklch(0.21 0.012 250 / 0.08)' : 'none',
                      }}
                    >
                      {s === 'mine' ? 'Mine' : 'All'}
                    </button>
                  ))}
                </div>
                <span className="muted" style={{ fontSize: 12 }}>
                  {scope === 'mine' ? 'Only your assignments' : 'All open phases'}
                </span>
              </div>

              <div>
                <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>Customer</label>
                <select
                  className="input"
                  value={customerId == null ? '' : String(customerId)}
                  onChange={e => {
                    const v = e.target.value
                    setCustomerId(v === '' ? null : (isNaN(Number(v)) ? v : Number(v)))
                    setProjectId(null); setPhaseId(null); setActivityId(null)
                  }}
                  style={{ width: '100%' }}
                >
                  <option value="">{workTree.length === 0 ? 'Loading…' : 'Select customer…'}</option>
                  {workTree.map(c => (
                    <option key={String(c.id)} value={String(c.id)}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>Project</label>
                <select
                  className="input"
                  value={projectId ?? ''}
                  onChange={e => {
                    setProjectId(e.target.value ? Number(e.target.value) : null)
                    setPhaseId(null); setActivityId(null)
                  }}
                  disabled={!customer}
                  style={{ width: '100%' }}
                >
                  <option value="">
                    {!customer ? 'Pick a customer first' : 'Select project…'}
                  </option>
                  {(customer?.projects ?? []).map(p => (
                    <option key={p.id} value={p.id}>
                      {p.certified ? '✓ ' : ''}{p.name}
                    </option>
                  ))}
                </select>
                {project?.certified && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="badge badge-ok" style={{ fontSize: 10 }}>Certified</span>
                    <span className="muted" style={{ fontSize: 11 }}>
                      hours auto-sync to TimeTracker
                    </span>
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>Phase</label>
                <select
                  className="input"
                  value={phaseId ?? ''}
                  onChange={e => { setPhaseId(e.target.value ? Number(e.target.value) : null); setActivityId(null) }}
                  disabled={!project}
                  style={{ width: '100%' }}
                >
                  <option value="">
                    {!project ? 'Pick a project first' : 'Select phase…'}
                  </option>
                  {(project?.phases ?? []).map(ph => (
                    <option key={ph.id} value={ph.id}>{ph.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>Activity</label>
                <select
                  className="input"
                  value={activityId ?? ''}
                  onChange={e => setActivityId(e.target.value ? Number(e.target.value) : null)}
                  disabled={!phase}
                  style={{ width: '100%' }}
                >
                  <option value="">
                    {!phase ? 'Pick a phase first' : 'Select activity…'}
                  </option>
                  {(phase?.activities ?? []).map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>

              {workIsDuplicate && (
                <p style={{ fontSize: 12, color: 'var(--ns-err)', marginTop: 4 }}>
                  This project + activity combination is already in the view.
                </p>
              )}
            </>
          )}

          {mode === 'leave' && (
            <div>
              <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>
                Leave type <span style={{ color: 'var(--ns-err)' }}>*</span>
              </label>
              <select
                className="input"
                value={leaveCode}
                onChange={e => setLeaveCode(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Select leave type…</option>
                {leaveTypes.map(lt => (
                  <option key={lt.code} value={lt.code}>{lt.label} ({lt.code})</option>
                ))}
              </select>
              {leaveIsDuplicate && (
                <p style={{ fontSize: 12, color: 'var(--ns-err)', marginTop: 4 }}>
                  This leave type is already in the week.
                </p>
              )}
              <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
                Leave hours count toward your weekly total but not toward billable hours.
                Absence entries sync to Fortnox payroll when an admin runs payroll export.
              </p>
            </div>
          )}

        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--ns-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-brand" onClick={handleAdd} disabled={!canAdd}>Add row</button>
        </div>
      </div>
    </>
  )
}

// ─── Approvals view ───────────────────────────────────────────────────────────

function ApprovalsView({ canSyncPayroll }: { canSyncPayroll: boolean }) {
  const queryClient   = useQueryClient()
  const [rejectTarget,  setRejectTarget]  = useState<{ weekId: number; staffName: string } | null>(null)
  const [rejectNote,    setRejectNote]    = useState('')
  const [invoiceTarget, setInvoiceTarget] = useState<{ weekId: number; staffName: string } | null>(null)
  const [invRef,        setInvRef]        = useState('')
  const [payrollResult, setPayrollResult] = useState<{ weekId: number; result: PayrollSyncResult } | null>(null)

  const { data: approvals = [], isLoading } = useQuery({
    queryKey: ['time', 'approvals'],
    queryFn:  () => api.get<ApprovalRow[]>('/time/approvals'),
  })

  const approveMutation = useMutation({
    mutationFn: (weekId: number) => api.post<unknown>(`/time/weeks/${weekId}/approve`, {}),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['time', 'approvals'] }),
  })

  const rejectMutation = useMutation({
    mutationFn: ({ weekId, note }: { weekId: number; note: string }) =>
      api.post<unknown>(`/time/weeks/${weekId}/reject`, { note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time', 'approvals'] })
      setRejectTarget(null); setRejectNote('')
    },
  })

  const invoiceMutation = useMutation({
    mutationFn: ({ weekId, fortnoxInvRef }: { weekId: number; fortnoxInvRef: string }) =>
      api.post<unknown>(`/time/weeks/${weekId}/mark-invoiced`, { fortnoxInvRef }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time', 'approvals'] })
      setInvoiceTarget(null); setInvRef('')
    },
  })

  const payrollMutation = useMutation({
    mutationFn: (weekId: number) => api.post<PayrollSyncResult>(`/time/weeks/${weekId}/sync-payroll`, {}),
    onSuccess: (result, weekId) => {
      queryClient.invalidateQueries({ queryKey: ['time', 'approvals'] })
      setPayrollResult({ weekId, result })
    },
  })

  if (isLoading) return <div className="muted" style={{ textAlign: 'center', paddingTop: 32, fontSize: 13 }}>Loading…</div>

  const pending = approvals.filter(a => a.status === 'submitted')
  const history = approvals.filter(a => a.status !== 'submitted')

  return (
    <>
      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Submitted by</th>
              <th>Week</th>
              <th style={{ width: 100, textAlign: 'right' }}>Hours</th>
              <th style={{ width: 100, textAlign: 'right' }}>Billable</th>
              <th style={{ width: 80,  textAlign: 'right' }}>Util</th>
              <th style={{ width: 110 }}>Status</th>
              <th style={{ width: 260 }} />
            </tr>
          </thead>
          <tbody>
            {approvals.length === 0 && (
              <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>No submissions yet.</td></tr>
            )}
            {pending.map(a => (
              <ApprovalRowItem
                key={a.weekId} row={a}
                canSyncPayroll={canSyncPayroll}
                onApprove={() => approveMutation.mutate(a.weekId)}
                onReject={() => setRejectTarget({ weekId: a.weekId, staffName: a.staffName })}
                onMarkInvoiced={() => setInvoiceTarget({ weekId: a.weekId, staffName: a.staffName })}
                onSyncPayroll={() => payrollMutation.mutate(a.weekId)}
                isSyncingPayroll={payrollMutation.isPending && payrollMutation.variables === a.weekId}
              />
            ))}
            {history.length > 0 && pending.length > 0 && (
              <tr><td colSpan={7} style={{ padding: '4px 8px', background: 'var(--ns-surface-2)', fontSize: 11, color: 'var(--ns-text-muted)' }}>History</td></tr>
            )}
            {history.map(a => (
              <ApprovalRowItem
                key={a.weekId} row={a}
                canSyncPayroll={canSyncPayroll}
                onApprove={() => approveMutation.mutate(a.weekId)}
                onReject={() => setRejectTarget({ weekId: a.weekId, staffName: a.staffName })}
                onMarkInvoiced={() => setInvoiceTarget({ weekId: a.weekId, staffName: a.staffName })}
                onSyncPayroll={() => payrollMutation.mutate(a.weekId)}
                isSyncingPayroll={payrollMutation.isPending && payrollMutation.variables === a.weekId}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Reject dialog */}
      {rejectTarget && (
        <Dialog onClose={() => setRejectTarget(null)}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Reject {rejectTarget.staffName}'s week</h3>
          <p className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>The staff member will be asked to revise and resubmit.</p>
          <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>Reason (optional)</label>
          <textarea
            className="input"
            value={rejectNote}
            onChange={e => setRejectNote(e.target.value)}
            rows={3}
            style={{ width: '100%', resize: 'vertical' }}
            placeholder="e.g. Missing project codes for Monday and Tuesday"
            autoFocus
          />
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => setRejectTarget(null)}>Cancel</button>
            <button
              className="btn"
              style={{ background: 'var(--ns-err)', color: 'white', borderColor: 'var(--ns-err)' }}
              onClick={() => rejectMutation.mutate({ weekId: rejectTarget.weekId, note: rejectNote })}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? 'Rejecting…' : 'Reject'}
            </button>
          </div>
        </Dialog>
      )}

      {/* Mark invoiced dialog */}
      {invoiceTarget && (
        <Dialog onClose={() => setInvoiceTarget(null)}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Mark {invoiceTarget.staffName}'s week as invoiced</h3>
          <p className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>This permanently locks the week. No further edits will be possible.</p>
          <label style={{ fontSize: 12.5, fontWeight: 500, display: 'block', marginBottom: 6 }}>Fortnox invoice reference (optional)</label>
          <input
            className="input mono"
            value={invRef}
            onChange={e => setInvRef(e.target.value)}
            style={{ width: '100%' }}
            placeholder="e.g. 1042"
            autoFocus
          />
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => setInvoiceTarget(null)}>Cancel</button>
            <button
              className="btn btn-brand"
              onClick={() => invoiceMutation.mutate({ weekId: invoiceTarget.weekId, fortnoxInvRef: invRef })}
              disabled={invoiceMutation.isPending}
            >
              {invoiceMutation.isPending ? 'Saving…' : 'Mark invoiced'}
            </button>
          </div>
        </Dialog>
      )}

      {/* Payroll sync result dialog */}
      {payrollResult && (
        <Dialog onClose={() => setPayrollResult(null)}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Payroll sync complete</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            <div className="row" style={{ gap: 8 }}>
              <Check size={14} style={{ color: 'var(--ns-ok)', flexShrink: 0, marginTop: 1 }} />
              <span>{payrollResult.result.synced} entries synced to Fortnox</span>
            </div>
            {payrollResult.result.skipped > 0 && (
              <div className="row" style={{ gap: 8 }}>
                <AlertTriangle size={14} style={{ color: 'var(--ns-warn)', flexShrink: 0, marginTop: 1 }} />
                <span>{payrollResult.result.skipped} entries skipped</span>
              </div>
            )}
            {payrollResult.result.errors.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ns-err)', marginBottom: 4 }}>Errors:</div>
                {payrollResult.result.errors.map((e, i) => (
                  <div key={i} className="mono" style={{ fontSize: 11, color: 'var(--ns-text-muted)', marginBottom: 2 }}>{e}</div>
                ))}
              </div>
            )}
          </div>
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-brand" onClick={() => setPayrollResult(null)}>Done</button>
          </div>
        </Dialog>
      )}
    </>
  )
}

function Dialog({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 420, background: 'var(--ns-surface)', borderRadius: 10,
        border: '1px solid var(--ns-border)', zIndex: 50, padding: 24,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}>
        {children}
      </div>
    </>
  )
}

function ApprovalRowItem({ row, canSyncPayroll, onApprove, onReject, onMarkInvoiced, onSyncPayroll, isSyncingPayroll }: {
  row:              ApprovalRow
  canSyncPayroll:   boolean
  onApprove:        () => void
  onReject:         () => void
  onMarkInvoiced:   () => void
  onSyncPayroll:    () => void
  isSyncingPayroll: boolean
}) {
  const util      = row.workedHrs > 0 ? (row.billableHrs / row.workedHrs) * 100 : 0
  const initials  = row.staffName.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
  const weekLabel = `W${getISOWeek(new Date(row.weekStart))}`

  return (
    <tr>
      <td>
        <div className="row" style={{ gap: 8 }}>
          <div className="avatar sm">{initials}</div>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{row.staffName}</span>
        </div>
      </td>
      <td className="muted" style={{ fontSize: 12.5 }}>{weekLabel} — {row.weekStart}</td>
      <td className="mono tabular" style={{ textAlign: 'right' }}>{row.workedHrs.toFixed(1)}</td>
      <td className="mono tabular" style={{ textAlign: 'right' }}>{row.billableHrs.toFixed(1)}</td>
      <td className="mono tabular" style={{ textAlign: 'right', color: util >= 80 ? 'var(--ns-ok)' : 'var(--ns-text-muted)' }}>
        {util.toFixed(0)}%
      </td>
      <td>
        {row.status === 'submitted' && <span className="badge badge-warn">Pending</span>}
        {row.status === 'approved'  && !row.invoicedAt && <span className="badge badge-ok">Approved</span>}
        {row.status === 'approved'  && row.invoicedAt  && <span className="badge badge-ok"><Receipt size={10} style={{ display: 'inline', marginRight: 3 }} />Invoiced</span>}
        {row.status === 'rejected'  && <span className="badge badge-err">Rejected</span>}
      </td>
      <td>
        {row.status === 'submitted' ? (
          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
            <button className="btn btn-sm btn-ghost" style={{ gap: 5 }} onClick={onReject}>
              <X size={12} />Reject
            </button>
            <button className="btn btn-sm btn-brand" style={{ gap: 5 }} onClick={onApprove}>
              <Check size={12} />Approve
            </button>
          </div>
        ) : row.status === 'approved' ? (
          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
            {!row.invoicedAt && (
              <button className="btn btn-sm btn-ghost" style={{ gap: 5 }} onClick={onMarkInvoiced}>
                <Receipt size={12} />Mark invoiced
              </button>
            )}
            {canSyncPayroll && (
              <button
                className="btn btn-sm btn-ghost"
                style={{ gap: 5, color: row.payrollSyncedAt ? 'var(--ns-ok)' : undefined }}
                onClick={onSyncPayroll}
                disabled={isSyncingPayroll}
                title={row.payrollSyncedAt ? `Last synced ${row.payrollSyncedAt.slice(0, 10)}` : 'Push hours to Fortnox payroll'}
              >
                <RefreshCw size={12} style={isSyncingPayroll ? { animation: 'spin 1s linear infinite' } : undefined} />
                {isSyncingPayroll ? 'Syncing…' : row.payrollSyncedAt ? 'Re-sync payroll' : 'Sync payroll'}
              </button>
            )}
          </div>
        ) : (
          <span className="muted" style={{ fontSize: 11.5, display: 'block', textAlign: 'right' }}>
            {row.invoicedAt ? `Invoiced · ${row.invoicedAt.slice(0, 10)}` : `Rejected · ${row.weekStart}`}
          </span>
        )}
      </td>
    </tr>
  )
}

function getISOWeek(date: Date): number {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7))
  const week1 = new Date(d.getFullYear(), 0, 4)
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
}

// ─── Utilisation view ─────────────────────────────────────────────────────────

function UtilisationGroup({ rows }: { rows: UtilisationRow[] }) {
  const deptAvg = rows.length > 0
    ? Math.round(rows.reduce((s, r) => s + (r.targetHrs > 0 ? (r.workedHrs / r.targetHrs) * 100 : 0), 0) / rows.length)
    : 0
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map(r => {
        const util     = r.targetHrs > 0 ? (r.workedHrs / r.targetHrs) * 100 : 0
        const barColor = util >= 75 ? 'var(--ns-brand-strong)' : util >= 50 ? 'var(--ns-warn)' : 'var(--ns-err)'
        const initials = r.staffName.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
        return (
          <div key={r.staffId} className="row" style={{ gap: 12 }}>
            <div className="row" style={{ width: 200, gap: 8, flexShrink: 0 }}>
              <div className="avatar sm">{initials}</div>
              <div style={{ overflow: 'hidden' }}>
                <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {r.staffName}
                </div>
                <div className="muted" style={{ fontSize: 11 }}>{r.billableHrs.toFixed(1)}h billable</div>
              </div>
            </div>
            <div style={{ flex: 1, position: 'relative', height: 22, background: 'var(--ns-surface-3)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${Math.min(100, util)}%`, background: barColor,
                borderRadius: 4, transition: 'width 0.3s',
              }} />
              <div className="tabular" style={{
                position: 'absolute', left: 8, top: 0, bottom: 0,
                display: 'flex', alignItems: 'center',
                fontSize: 11.5, fontWeight: 600,
                color: util >= 30 ? 'white' : 'var(--ns-text)',
              }}>
                {r.workedHrs.toFixed(1)}h / {r.targetHrs}h
              </div>
            </div>
            <div className="mono tabular" style={{ width: 50, textAlign: 'right', fontSize: 13, fontWeight: 600 }}>
              {util.toFixed(0)}%
            </div>
          </div>
        )
      })}
      <div className="row" style={{ justifyContent: 'flex-end', fontSize: 11.5, paddingTop: 4, borderTop: '1px dashed var(--ns-border)' }}>
        <span className="muted" style={{ marginRight: 8 }}>Dept avg</span>
        <span className="mono tabular" style={{ fontWeight: 600, width: 50, textAlign: 'right' }}>{deptAvg}%</span>
      </div>
    </div>
  )
}

function UtilisationView({ weekStartStr }: { weekStartStr: string }) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['time', 'utilisation', weekStartStr],
    queryFn:  () => api.get<UtilisationRow[]>(`/time/utilisation?weekStart=${weekStartStr}`),
  })

  if (isLoading) return <div className="muted" style={{ textAlign: 'center', paddingTop: 32, fontSize: 13 }}>Loading…</div>

  const teamAvg = rows.length > 0
    ? Math.round(rows.reduce((s, r) => s + (r.targetHrs > 0 ? (r.workedHrs / r.targetHrs) * 100 : 0), 0) / rows.length)
    : 0

  const grouped = rows.reduce<Record<string, UtilisationRow[]>>((acc, r) => {
    const key = r.department ?? 'No department'
    ;(acc[key] ??= []).push(r)
    return acc
  }, {})
  const departments = Object.keys(grouped).sort()
  const isGrouped   = departments.length > 1

  return (
    <div className="card" style={{ padding: 16 }}>
      {rows.length === 0 ? (
        <div className="muted" style={{ textAlign: 'center', padding: 32, fontSize: 13 }}>No data for this week.</div>
      ) : isGrouped ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {departments.map(dept => (
            <div key={dept}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ns-text-muted)', marginBottom: 12 }}>
                {dept}
              </div>
              <UtilisationGroup rows={grouped[dept]} />
            </div>
          ))}
        </div>
      ) : (
        <UtilisationGroup rows={rows} />
      )}
      <div className="divider" />
      <div className="row" style={{ justifyContent: 'space-between', fontSize: 12 }}>
        <span className="muted">Overall avg this week</span>
        <span className="mono tabular" style={{ fontWeight: 600 }}>{teamAvg}%</span>
      </div>
    </div>
  )
}

// ─── Per-entry list panel (WI-11825) ─────────────────────────────────────────
// Drawer that lists every entry in the current view's date range. Click a row
// to open the shared TimeEntryEditor; multi-select rows that share the same
// (project, phase, activity, date) bucket and Merge them into one. Used to
// edit individual timer-driven entries that the grid view collapses.

function entryGroupKey(e: TimeEntry): string {
  return `${e.projectId ?? 'n'}::${e.phaseId ?? 'n'}::${e.activityId ?? 'n'}::${e.entryDate}`
}

function EntriesPanel({ entries, weekStatus, isReadOnly, onClose }: {
  entries:    TimeEntry[]
  weekStatus: WeekStatusValue
  isReadOnly: boolean
  onClose:    () => void
}) {
  const queryClient = useQueryClient()
  const [timerState] = useTimerState()
  const { resumeEntry } = useTimerActions()
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)
  const [selectedIds,  setSelectedIds]  = useState<Set<number>>(new Set())
  const resumableIds = useMemo(
    () => timerState.running ? new Set<number>() : new Set(timerState.timerEntryIds),
    [timerState.running, timerState.timerEntryIds],
  )

  // Sorted: latest first.
  const sorted = useMemo(
    () => [...entries].sort((a, b) =>
      a.entryDate === b.entryDate ? (b.id - a.id) : (b.entryDate.localeCompare(a.entryDate))),
    [entries],
  )

  // Once the user picks the first entry, lock the merge group to its
  // (project, phase, activity, date). Other rows render disabled.
  const lockKey: string | null = useMemo(() => {
    for (const e of sorted) if (selectedIds.has(e.id)) return entryGroupKey(e)
    return null
  }, [sorted, selectedIds])

  const selectedEntries = sorted.filter(e => selectedIds.has(e.id))
  const canMerge        = selectedEntries.length >= 2 && !isReadOnly

  const merge = useMutation({
    mutationFn: (ids: number[]) =>
      api.post<TimeEntryMergeResponse>('/time/entries-merge', { entryIds: ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time', 'entries'] })
      setSelectedIds(new Set())
    },
  })

  const toggle = (id: number) =>
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer" style={{ width: 640 }}>
        <div className="drawer-head">
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>
              Manage entries · {entries.length} in view
            </h2>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              Click a row to edit hours, notes, or invoice text. Tick rows on the same task to merge them.
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="drawer-body" style={{ padding: 0 }}>
          {selectedIds.size > 0 && (
            <div className="te-select-bar" style={{ margin: 16, marginBottom: 0 }}>
              <span className="te-select-count">{selectedIds.size} selected</span>
              {selectedIds.size === 1 && (
                <span className="muted" style={{ fontSize: 11.5 }}>
                  Pick one more on the same task to merge
                </span>
              )}
              <span className="te-select-spacer" />
              <button className="btn btn-ghost" onClick={() => setSelectedIds(new Set())}>Clear</button>
              <button
                className="btn btn-brand"
                disabled={!canMerge || merge.isPending}
                onClick={() => merge.mutate(selectedEntries.map(e => e.id))}
                title={canMerge ? 'Merge into one entry' : 'Pick 2+ entries on the same task'}
              >
                <Combine size={14} />
                {merge.isPending ? 'Merging…' : `Merge (${selectedEntries.length})`}
              </button>
            </div>
          )}

          {merge.isError && (
            <div className="te-error" style={{ margin: 16 }}>
              Merge failed: {(merge.error as Error).message}
            </div>
          )}

          {sorted.length === 0 ? (
            <div className="muted" style={{ textAlign: 'center', padding: 32, fontSize: 13 }}>
              No entries in the current view.
            </div>
          ) : (
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sorted.map(e => {
                const sel        = selectedIds.has(e.id)
                const matchesLock = lockKey === null || entryGroupKey(e) === lockKey
                const dim        = lockKey !== null && !matchesLock
                return (
                  <div
                    key={e.id}
                    className={`te-list-row${dim ? ' te-list-row-dim' : ''}`}
                    style={{
                      background:  sel ? 'var(--ns-brand-soft)' : 'var(--ns-surface-2)',
                      borderColor: sel ? 'var(--ns-brand)' : 'var(--ns-border)',
                    }}
                    title={dim ? 'Different project / phase / activity / date — clear the selection to pick this one' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={sel}
                      onChange={() => toggle(e.id)}
                      onClick={ev => ev.stopPropagation()}
                      disabled={isReadOnly || dim}
                      style={{ cursor: (isReadOnly || dim) ? 'not-allowed' : 'pointer' }}
                    />
                    <button
                      className="te-list-button"
                      onClick={() => setEditingEntry(e)}
                      disabled={isReadOnly}
                    >
                      <div className="te-list-line">
                        <div className="te-list-customer">
                          {e.customerName ?? <em>Internal</em>}
                          <span className="muted"> · {e.entryDate}</span>
                        </div>
                        <div className="te-list-hours mono tabular">
                          {e.workedHrs.toFixed(2)}h
                          {e.billableHrs !== e.workedHrs && (
                            <span className="muted" style={{ fontSize: 11 }}>
                              {' '}({e.billableHrs.toFixed(2)} bill.)
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="te-list-line2">
                        {[e.projectName, e.phaseName, e.activityName].filter(Boolean).join(' · ')
                          || (e.leaveLabel ?? '—')}
                      </div>
                      {(e.notes || e.invoiceText) && (
                        <div className="te-list-note">
                          {e.invoiceText && <span style={{ fontWeight: 500 }}>📄 {e.invoiceText}</span>}
                          {e.invoiceText && e.notes && <span> · </span>}
                          {e.notes && <span>{e.notes}</span>}
                        </div>
                      )}
                    </button>
                    {!isReadOnly && resumableIds.has(e.id) && (
                      <button
                        type="button"
                        className="mt-entry-resume"
                        onClick={ev => { ev.stopPropagation(); resumeEntry(e) }}
                        title="Resume timer on this entry"
                      >
                        <Play size={11} fill="currentColor" />
                        Resume
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {editingEntry && (
        <TimeEntryEditor
          entry={editingEntry}
          weekStatus={weekStatus}
          mode="drawer"
          onClose={() => setEditingEntry(null)}
        />
      )}
    </>
  )
}
